import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { api, type Me, type WsAsama, type WsIs, type WsSiparisler, type WsSiparisSatiri, type WsTalepler, type WsUyeler, type WsUyeSatiri } from './api';
import { OrdersPanel, ReleaseQueuePanel } from './Commerce';
import { WS } from './metin/web-sitesi';
import { GecikmeEtiketi, YasCubugu } from './WebSitesiGenel';
import { AsamaBloklari } from './WebSitesiGrafik';
import { AramaAlani, Bos, Dt, EslesmeEtiketi, Hata, Olcekli, Vurgu, useVeri } from './WebSitesiOrtak';
import type { SiparisSuzgeci, TalepSuzgeci, UyeSuzgeci } from './web-sitesi-durum';
import { SAYFA } from './web-sitesi-durum';
import { gunBae, tutarMetni, yasMetni } from './web-sitesi-bicim';

/**
 * Talepler, Üyeler ve Siparişler listeleri (taslak A, 07.10.2026).
 *
 * Her liste tek arama kutusu, tarih aralığı, süzgeç düğmeleri ve sunucudan gelen sayıları taşır;
 * süzme sunucuda yapılır (arama Türkçe katlamalı, bkz. `api/src/modules/webshop/arama.ts`).
 */

function Daha({ gosterilen, toplam, artir, testId }: { gosterilen: number; toplam: number; artir: () => void; testId: string }) {
  if (gosterilen >= toplam) return null;
  return (
    <div style={{ marginTop: 'var(--space-md)', textAlign: 'center' }}>
      <button type="button" className="btn sm" data-testid={testId} onClick={artir}>
        {WS.talepler.daha}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Talepler
// ---------------------------------------------------------------------------

export function Talepler({ s, setS, ac, tazele }: { s: TalepSuzgeci; setS: Dispatch<SetStateAction<TalepSuzgeci>>; ac: (i: WsIs) => void; tazele: number }) {
  const r = useVeri<WsTalepler>(
    () => api.webPanelInbox({ q: s.q, from: s.from, to: s.to, tur: s.tur, tab: s.tab, over: s.over ? '1' : '0', sort: s.sort, pageSize: s.adet }),
    [s.q, s.from, s.to, s.tur, s.tab, s.over, s.sort, s.adet, tazele],
  );
  const v = r.veri;
  const degis = (k: Partial<TalepSuzgeci>) => setS((o) => ({ ...o, adet: SAYFA, ...k }));
  const turler: [TalepSuzgeci['tur'], string, number][] = v
    ? [['hepsi', WS.talepler.tumu, v.counts.types.hepsi], ['destek', WS.tur.destek, v.counts.types.destek], ['on', WS.tur.on, v.counts.types.on], ['vize', WS.tur.vize, v.counts.types.vize]]
    : [];
  if (v && s.tur === 'onvize') turler.push(['onvize', WS.talepler.onVize, v.counts.types.onvize]);
  if (v) turler.push(['sistem', WS.talepler.sistem, v.counts.types.sistem]);
  if (v) turler.push(['spam', WS.talepler.spam, v.counts.types.spam]);
  const supheSayisi = v && s.tur !== 'spam' && s.tur !== 'sistem' ? v.counts.suspicious : 0;
  const bos = (() => {
    if (!v) return null;
    if (s.tur === 'spam') return WS.talepler.spamBos;
    if (s.tab === 'kapali') return WS.talepler.kapaliBos;
    if (s.tab === 'musteri') return WS.talepler.musteriBos;
    return WS.talepler.suzgecBos;
  })();
  return (
    <section className="card" data-testid="ws-talepler">
      <div className="card-head">
        <div>
          <h3>{WS.talepler.baslik}</h3>
          <p className="sub">{WS.talepler.alt}</p>
        </div>
        <span className="count-badge" data-testid="ws-talepler-adet">{WS.talepler.adet(v?.total ?? 0)}</span>
      </div>
      <div className="filters">
        <span className="lbl">{WS.talepler.turEtiket}</span>
        {turler.map(([k, ad, n]) => (
          <button key={k} type="button" className="chip" aria-pressed={s.tur === k} data-testid={`ws-tur-${k}`} onClick={() => degis({ tur: k })}>
            {ad}
            <b>{n}</b>
          </button>
        ))}
        <button type="button" className="chip" aria-pressed={s.over} data-testid="ws-asan" onClick={() => degis({ over: !s.over })}>
          {WS.talepler.esigiAsanlar}
          <b>{v?.counts.overdue ?? 0}</b>
        </button>
        <AramaAlani q={s.q} setQ={(q) => degis({ q })} from={s.from} to={s.to} setAralik={(from, to) => degis({ from, to })} placeholder={WS.ara.talep} testId="ws-talep" />
        <select className="sortsel" aria-label={WS.talepler.sirala} value={s.sort} data-testid="ws-talep-sirala" onChange={(e) => degis({ sort: e.target.value === 'yeni' ? 'yeni' : 'yas' })}>
          <option value="yas">{WS.talepler.enUzun}</option>
          <option value="yeni">{WS.talepler.enYeni}</option>
        </select>
      </div>
      {s.tur === 'sistem' ? <p className="cnote" data-testid="ws-sistem-not">{WS.talepler.sistemAlt}</p> : null}
      {s.tur === 'spam' ? <p className="cnote" data-testid="ws-spam-not">{WS.talepler.spamAlt}</p> : null}
      {supheSayisi > 0 ? <p className="cnote" data-testid="ws-suphe-not">{WS.talepler.suphe(supheSayisi)}</p> : null}
      {s.tur === 'spam' ? null : (
        <div className="tabs" role="tablist" aria-label={WS.talepler.durumAria}>
          {(['sende', 'musteri', 'kapali'] as const).map((t) => (
            <button key={t} type="button" className="tab" role="tab" aria-selected={s.tab === t} data-testid={`ws-tab-${t}`} onClick={() => degis({ tab: t })}>
              {WS.sahip[t]}
              <span className="n">{v?.counts.tabs[t] ?? 0}</span>
            </button>
          ))}
        </div>
      )}
      {r.hata && !v ? <Hata mesaj={r.hata} yenile={r.yenile} /> : null}
      {v && v.items.length === 0 ? <Bos baslik={bos![0]} alt={bos![1]} dugme={null} /> : null}
      {v && v.items.length > 0 ? (
        <Dt
          testId="ws-talep-tablo"
          kolonlar={[WS.talepler.kim, WS.talepler.ne, WS.talepler.tur, WS.talepler.bekleme, WS.talepler.esik, WS.talepler.sonraki]}
          satirlar={v.items.map((i) => ({
            key: i.key,
            gec: i.overdue,
            testId: 'ws-talep-satir',
            ac: () => ac(i),
            hucreler: [
              <div className="who" key="k">
                <b><Vurgu metin={i.who} eslesme={i.eslesme} alan="ad" /></b>
                <span>{i.shortReference}</span>
                <EslesmeEtiketi eslesme={i.eslesme} gorunen={['ad', 'ne']} />
              </div>,
              <div key="n">
                <Vurgu metin={i.what} eslesme={i.eslesme} alan="ne" />
                {i.iletisimYok ? <div className="chips"><span className="tag warn" data-testid="ws-talep-iletisim-yok">{WS.onTalep.yokEtiket}</span></div> : null}
                {i.destek ? (
                  <div className="chips" data-testid="ws-talep-destek">
                    <span className="tag mute">{i.destek.kategori ? WS.destek.kategori[i.destek.kategori] : WS.destek.akisKisa[i.destek.akis]}</span>
                    {i.destek.sinif !== 'GERCEK' ? <span className={i.destek.sinif === 'SPAM' ? 'tag bad' : 'tag warn'} data-testid="ws-talep-sinif">{WS.talepler.sinifEtiket[i.destek.sinif]}</span> : null}
                  </div>
                ) : null}
              </div>,
              <span className="tag" key="t">{WS.tur[i.tur]}</span>,
              <div key="b">
                {i.owner === 'kapali' ? (
                  <span className="cnote" style={{ margin: 0 }}>{WS.siparisler.kapandi}</span>
                ) : (
                  <>
                    <div className="age"><b>{yasMetni(i.ageDays)}</b><YasCubugu yas={i.ageDays} esik={i.threshold} /></div>
                    <GecikmeEtiketi yas={i.ageDays} esik={i.threshold} />
                  </>
                )}
              </div>,
              WS.talepler.esikGun(i.threshold),
              i.next,
            ],
          }))}
        />
      ) : null}
      {v ? <Daha gosterilen={v.items.length} toplam={v.total} artir={() => setS((o) => ({ ...o, adet: o.adet + SAYFA }))} testId="ws-talep-daha" /> : null}
      {v ? (
        <p className="tnote">
          {WS.talepler.not(v.thresholds.SUPPORT, v.thresholds.DEMAND, v.thresholds.VISA, v.thresholds.ORDER, v.hiddenSystemMessages, v.hiddenSpam)}
          {v.truncated ? ` ${WS.kesildi}` : ''}
          {v.visaIncluded ? '' : ` ${WS.vizeYetki}`}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Üyeler
// ---------------------------------------------------------------------------

function GorulmeHucresi({ m }: { m: WsUyeSatiri }) {
  if (m.neverLoggedIn) return <span className="tag mute">{WS.uyeler.hicOturum}</span>;
  if (m.daysSinceSeen === null) return <span className="tag mute">{WS.uyeler.bilinmiyor}</span>;
  const w = 72;
  const x = (Math.min(m.daysSinceSeen, 40) / 40) * w;
  return (
    <div className="seen">
      <b>{WS.gunOnce(Math.round(m.daysSinceSeen))}</b>
      <svg width={w} height={12} viewBox={`0 0 ${w} 12`} aria-hidden="true">
        <rect x={0} y={5} width={w} height={2} fill="var(--mute-soft)" />
        <rect x={(7 / 40) * w} y={2} width={1} height={8} fill="var(--line-strong)" />
        <rect x={(30 / 40) * w} y={2} width={1} height={8} fill="var(--danger)" />
        <circle cx={x} cy={6} r={4} fill={m.daysSinceSeen > 30 ? 'var(--danger)' : 'var(--accent)'} stroke="var(--surface)" strokeWidth={1.5} />
      </svg>
    </div>
  );
}

export function bultenEtiketi(durum: string | null): { metin: string; sinif: string } {
  switch (durum) {
    case 'CONFIRMED':
      return { metin: WS.uyeler.onayli, sinif: 'tag ok' };
    case 'PENDING':
      return { metin: WS.uyeler.bekliyor, sinif: 'tag warn' };
    case 'SUPPRESSED':
      return { metin: WS.uyeler.engelli, sinif: 'tag bad' };
    case 'UNSUBSCRIBED':
      return { metin: WS.uyeler.abonelikIptal, sinif: 'tag mute' };
    default:
      return { metin: WS.uyeler.kayitYok, sinif: 'tag mute' };
  }
}

export function Uyeler({ s, setS, git }: { s: UyeSuzgeci; setS: Dispatch<SetStateAction<UyeSuzgeci>>; git: (h: string) => void }) {
  const r = useVeri<WsUyeler>(() => api.webPanelMembers({ q: s.q, from: s.from, to: s.to, f: s.f, sort: s.sort, pageSize: s.adet }), [s.q, s.from, s.to, s.f, s.sort, s.adet]);
  const v = r.veri;
  const degis = (k: Partial<UyeSuzgeci>) => setS((o) => ({ ...o, adet: SAYFA, ...k }));
  const cipler: [UyeSuzgeci['f'], string, number][] = v
    ? [['hepsi', WS.uyeler.hepsi, v.counts.hepsi], ['dogrulanmamis', WS.uyeler.dogrulanmamis, v.counts.dogrulanmamis], ['dogrulanmis', WS.uyeler.dogrulanmis, v.counts.dogrulanmis], ['oturumsuz', WS.uyeler.oturumsuz, v.counts.oturumsuz], ['siparisli', WS.uyeler.siparisli, v.counts.siparisli]]
    : [];
  return (
    <section className="card" data-testid="ws-uyeler">
      <div className="card-head">
        <div>
          <h3>{WS.uyeler.baslik}</h3>
          <p className="sub">{WS.uyeler.alt(v?.counts.total ?? 0)}</p>
        </div>
        <span className="count-badge" data-testid="ws-uyeler-adet">{WS.uyeler.adet(v?.total ?? 0)}</span>
      </div>
      <div className="filters">
        {cipler.map(([k, ad, n]) => (
          <button key={k} type="button" className="chip" aria-pressed={s.f === k} data-testid={`ws-uf-${k}`} onClick={() => degis({ f: k })}>
            {ad}
            <b>{n}</b>
          </button>
        ))}
        {s.f === 'kayip-odemeli' ? (
          <button type="button" className="chip" aria-pressed data-testid="ws-uf-kayip-odemeli" onClick={() => degis({ f: 'hepsi' })}>
            {WS.uyeler.kayipOdemeli(7)}
          </button>
        ) : null}
        <AramaAlani q={s.q} setQ={(q) => degis({ q })} from={s.from} to={s.to} setAralik={(from, to) => degis({ from, to })} placeholder={WS.ara.uye} testId="ws-uye" />
        <select className="sortsel" aria-label={WS.uyeler.sirala} value={s.sort} data-testid="ws-uye-sirala" onChange={(e) => degis({ sort: e.target.value === 'seen' ? 'seen' : 'reg' })}>
          <option value="reg">{WS.uyeler.sIlk}</option>
          <option value="seen">{WS.uyeler.sSon}</option>
        </select>
      </div>
      {r.hata && !v ? <Hata mesaj={r.hata} yenile={r.yenile} /> : null}
      {v && v.items.length === 0 ? <Bos baslik={WS.uyeler.bos[0]} alt={WS.uyeler.bos[1]} /> : null}
      {v && v.items.length > 0 ? (
        <Dt
          testId="ws-uye-tablo"
          kolonlar={[WS.uyeler.kim, WS.uyeler.uyelik, WS.uyeler.gorulme, WS.uyeler.eposta, WS.uyeler.siparis, WS.uyeler.bulten, WS.uyeler.crm]}
          satirlar={v.items.map((m) => {
            const bulten = bultenEtiketi(m.newsletter);
            return {
              key: m.externalId,
              testId: 'ws-uye-satir',
              ac: () => git(`person:${m.externalId}`),
              hucreler: [
                <div className="who" key="k">
                  <b><Vurgu metin={m.name} eslesme={m.eslesme} alan="ad" /></b>
                  <span style={{ display: 'block' }}><Vurgu metin={m.email} eslesme={m.eslesme} alan="eposta" /></span>
                  <span style={{ display: 'block' }}>{m.hasPhone ? WS.uyeler.kademeTelefon(m.tier) : WS.uyeler.kademe(m.tier)}</span>
                  <EslesmeEtiketi eslesme={m.eslesme} gorunen={['ad', 'eposta']} />
                </div>,
                gunBae(m.registeredAt),
                <GorulmeHucresi key="g" m={m} />,
                m.emailVerified ? <span className="tag ok" key="e">{WS.uyeler.dogrulandi}</span> : <span className="tag mute" key="e">{WS.uyeler.dogrulanmadi}</span>,
                <span key="s">{m.orderCount}{m.orderCount ? <> <span className="cnote" style={{ margin: 0 }}>{WS.uyeler.eslesmeyle}</span></> : null}</span>,
                <span className={bulten.sinif} key="b">{bulten.metin}</span>,
                m.master ? (
                  <span className="tag ok" key="c">{WS.uyeler.bagli(m.master.name ?? '')}</span>
                ) : m.reviewState === 'RESOLVED' ? (
                  <span className="tag mute" key="c">{WS.uyeler.ayriTutuldu}</span>
                ) : (
                  <span key="c">
                    <span className="tag warn">{WS.uyeler.eslesmeYok}</span>{' '}
                    <button type="button" className="btn sm" data-testid="ws-karar-ver" onClick={() => git(`person:${m.externalId}:karar`)}>{WS.uyeler.kararVer}</button>
                  </span>
                ),
              ],
            };
          })}
        />
      ) : null}
      {v ? <Daha gosterilen={v.items.length} toplam={v.total} artir={() => setS((o) => ({ ...o, adet: o.adet + SAYFA }))} testId="ws-uye-daha" /> : null}
      {v ? <p className="tnote">{WS.uyeler.not}{v.truncated ? ` ${WS.kesildi}` : ''}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Siparişler
// ---------------------------------------------------------------------------

function siparisDurumu(o: WsSiparisSatiri): { metin: string; sinif: string } {
  switch (o.state) {
    case 'PENDING_PAYMENT':
      return { metin: WS.siparisler.bekliyorEtiket, sinif: 'tag warn' };
    case 'AUTHORIZED':
      return { metin: WS.siparisler.provizyonda, sinif: 'tag warn' };
    case 'CANCELLED':
      return { metin: WS.siparisler.iptalEtiket, sinif: 'tag mute' };
    case 'PAID':
      return { metin: WS.siparisler.odendiEtiket, sinif: 'tag ok' };
    default:
      return { metin: WS.siparisler.iadeEtiket, sinif: 'tag mute' };
  }
}

function siparisAsamasi(r: WsSiparisler): WsAsama {
  const c = r.counts;
  const asan = c.bekleyen > 0;
  return {
    id: 'sip',
    title: WS.siparisler.asamaBaslik,
    sub: WS.siparisler.asamaAlt(c.hepsi),
    rows: [
      { label: WS.siparisler.durumSatir.hepsi, count: c.hepsi, highlight: false, key: 'hepsi' },
      { label: WS.siparisler.durumSatir.bekleyen, count: c.bekleyen, highlight: c.bekleyen > 0, key: 'bekleyen' },
      { label: WS.siparisler.durumSatir.odendi, count: c.odendi, highlight: false, key: 'odendi' },
      { label: WS.siparisler.durumSatir.iptal, count: c.iptal, highlight: false, key: 'iptal' },
    ],
    note: c.odendi === 0 ? WS.siparisler.asamaNotOdemeYok(c.bekleyen) : WS.siparisler.asamaNot(c.bekleyen, c.odendi),
    warn: asan,
    crm: null,
  };
}

export function Siparisler({ me, s, setS, ac, git }: { me: Me; s: SiparisSuzgeci; setS: Dispatch<SetStateAction<SiparisSuzgeci>>; ac: (o: WsSiparisSatiri) => void; git: (h: string) => void }) {
  // `rez` ayrı bir liste değil: v3'ün operasyon kuyruğu (ödemesi alınmış, tedarikçide rezervasyonu
  // yapılacak siparişler). Bu süzgeçte CRM'in kendi satırları yerine o kuyruk çiziliyor.
  const rezMi = s.f === 'rez';
  const rezOkur = me.permissions.includes('commerce:operations_read');
  const r = useVeri<WsSiparisler>(() => api.webPanelOrders({ q: s.q, from: s.from, to: s.to, f: rezMi ? 'hepsi' : s.f, who: s.who ?? undefined, pageSize: s.adet }), [s.q, s.from, s.to, s.f, s.who, s.adet]);
  const [rezSayisi, setRezSayisi] = useState<number | null>(null);
  useEffect(() => {
    if (!rezOkur) return;
    let iptal = false;
    api.releaseQueue({ limit: 1 }).then((q) => { if (!iptal) setRezSayisi(q.page.total); }, () => undefined);
    return () => { iptal = true; };
  }, [rezOkur]);
  const v = r.veri;
  const degis = (k: Partial<SiparisSuzgeci>) => setS((o) => ({ ...o, adet: SAYFA, ...k }));
  const cipler: [SiparisSuzgeci['f'], string, string | number][] = v
    ? [['hepsi', WS.siparisler.hepsi, v.counts.hepsi], ['bekleyen', WS.siparisler.bekleyen, v.counts.bekleyen], ['rez', WS.siparisler.rez, rezSayisi ?? WS.siparisler.rezSayiYok], ['geciken', WS.siparisler.geciken(v.threshold), v.counts.geciken], ['odendi', WS.siparisler.odendi, v.counts.odendi], ['iptal', WS.siparisler.iptal, v.counts.iptal]]
    : [];
  const stageAc = (_id: unknown, key: string) => degis({ f: (['hepsi', 'bekleyen', 'odendi', 'iptal'] as const).find((x) => x === key) ?? 'hepsi', who: null });
  const kisiAd = v?.items.find((i) => i.member?.externalId === s.who)?.member?.name ?? '';
  return (
    <>
      <section className="card" data-testid="ws-siparis-durum">
        <div className="card-head">
          <div>
            <h3>{WS.siparisler.durumBaslik}</h3>
            <p className="sub">{WS.siparisler.durumAlt(v?.totalAll ?? 0)}</p>
          </div>
          <span className="count-badge">{WS.siparisler.odendiRozet(v?.counts.odendi ?? 0)}</span>
        </div>
        {v ? <Olcekli>{(w) => <AsamaBloklari genislik={w} asamalar={[siparisAsamasi(v)]} kolon={1} ac={stageAc} />}</Olcekli> : null}
        {v ? (
          <p className="cnote">
            {WS.siparisler.paraBirimi(v.currencies.map((c) => `${c.code} ${c.count}`).join(', '))}
            {v.v3Count !== null && v.v3Count !== v.totalAll ? ` ${WS.siparisler.crmFark(v.totalAll, v.v3Count)}` : ''}
          </p>
        ) : null}
      </section>
      <section className="card" style={{ marginTop: 'var(--space-lg)' }} data-testid="ws-siparisler">
        <div className="card-head">
          <div>
            <h3>{WS.siparisler.baslik}</h3>
            <p className="sub">{WS.siparisler.alt}</p>
          </div>
          <span className="count-badge" data-testid="ws-siparis-adet">{WS.siparisler.adet(v?.total ?? 0)}</span>
        </div>
        <div className="filters">
          {cipler.map(([k, ad, n]) => (
            <button key={k} type="button" className="chip" aria-pressed={s.f === k} data-testid={`ws-sf-${k}`} onClick={() => degis({ f: k })}>
              {ad}
              <b>{n}</b>
            </button>
          ))}
          {s.who ? (
            <button type="button" className="chip" aria-pressed data-testid="ws-sf-kisi" onClick={() => degis({ who: null })}>
              {WS.siparisler.kisiSuzgeci(kisiAd)}
            </button>
          ) : null}
          <AramaAlani q={s.q} setQ={(q) => degis({ q })} from={s.from} to={s.to} setAralik={(from, to) => degis({ from, to })} placeholder={WS.ara.siparis} testId="ws-sip" />
        </div>
        {rezMi ? (
          <div data-testid="ws-rezervasyon">
            <p className="sub">{WS.siparisler.rezAlt}</p>
            {rezOkur ? <div className="workflow-stack"><ReleaseQueuePanel /><OrdersPanel /></div> : <Bos baslik={WS.siparisler.rezBaslik} alt={WS.siparisler.rezYetki} />}
          </div>
        ) : (
          <>
            {r.hata && !v ? <Hata mesaj={r.hata} yenile={r.yenile} /> : null}
            {v && v.items.length === 0 ? (s.f === 'odendi' ? <Bos baslik={WS.siparisler.odenenBosBaslik} alt={WS.siparisler.odenenBos(v.totalAll)} /> : <Bos baslik={WS.siparisler.suzgecBos[0]} alt={WS.siparisler.suzgecBos[1]} />) : null}
            {v && v.items.length > 0 ? (
              <Dt
                testId="ws-siparis-tablo"
                kolonlar={[WS.siparisler.siparis, WS.siparisler.durum, WS.siparisler.tutar, WS.siparisler.kisi, WS.siparisler.bekleme, WS.siparisler.verildi]}
                satirlar={v.items.map((o) => {
                  const d = siparisDurumu(o);
                  return {
                    key: o.reference,
                    gec: o.overdue,
                    testId: 'ws-siparis-satir',
                    ac: () => ac(o),
                    hucreler: [
                      <div className="who" key="k">
                        <b>{o.shortReference}</b>
                        <EslesmeEtiketi eslesme={o.eslesme} gorunen={['ad', 'eposta']} />
                      </div>,
                      <span className={d.sinif} key="d">{d.metin}</span>,
                      tutarMetni(o.amountMinor, o.currencyCode),
                      o.member ? (
                        <span key="u">
                          <button type="button" className="link-btn" onClick={() => git(`person:${o.member!.externalId}`)}>
                            <Vurgu metin={o.member.name} eslesme={o.eslesme} alan="ad" />
                          </button>{' '}
                          {o.member.guest ? <span className="cnote" style={{ margin: 0 }}>{WS.siparisler.misafirOlarak}</span> : null}
                        </span>
                      ) : (
                        <span key="u">{o.who ? <Vurgu metin={o.who} eslesme={o.eslesme} alan="ad" /> : WS.misafir}</span>
                      ),
                      o.waiting ? (
                        <div key="b"><div className="age"><b>{yasMetni(o.ageDays)}</b><YasCubugu yas={o.ageDays} esik={v.threshold} /></div><GecikmeEtiketi yas={o.ageDays} esik={v.threshold} /></div>
                      ) : (
                        <span className="cnote" style={{ margin: 0 }} key="b">{WS.siparisler.kapandi}</span>
                      ),
                      gunBae(o.placedAt),
                    ],
                  };
                })}
              />
            ) : null}
            {v ? <Daha gosterilen={v.items.length} toplam={v.total} artir={() => setS((o) => ({ ...o, adet: o.adet + SAYFA }))} testId="ws-siparis-daha" /> : null}
            {v?.truncated ? <p className="tnote">{WS.kesildi}</p> : null}
          </>
        )}
      </section>
    </>
  );
}
